import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback, ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/context/AuthContext';
import { useTime } from '../../../shared/context/TimeContext';
import stateService from '../services/stateService';
import timelineService from '../services/timelineService';
import defaultsService from '../services/defaultsService';
import timeSyncService from '../../sync/services/timeSyncService';
import {
  getLocalSubscriptions,
  addLocalSubscription,
  removeLocalSubscription,
  clearLocalSubscriptions
} from '../services/localSubscriptionService';
import {
  parseTimeToSeconds,
  getOccurrencesInWindow,
  determineActiveOccurrenceFromElements,
  calculateOccurrenceEndTimeFromElements,
  Element,
  ElementInput,
  Schedule,
  ScheduleEntry,
  ScheduledOccurrence
} from '@thekwallace/backtime-shared';
import { entriesToSchedule, scheduleToEntries, getClientId } from '../services/timelineService';
import { moveElement as moveElementOp, ungroup as ungroupOp, DropPosition } from '../utils/grouping';

// Timeline data from API
interface TimelineData {
  id: string;
  name?: string;
  ownerId?: string;
  isPublic?: boolean;
  publicCode?: string | null;
  elements?: Element[];
  scheduleEntries?: ScheduleEntry[];
  timeOffset?: number | null;
  selectedStartTime?: string | null;
  activeStartTime?: string | null;
  isAutoAdvanceEnabled?: boolean;
  autoAdvanceBuffer?: number;
  isSubscribed?: boolean;
  _reason?: string;
}

// Timeline list item for picker
export interface TimelineListItem {
  id: string;
  name: string;
  isLocal?: boolean;
  isSubscribed?: boolean;
  isLocalSubscription?: boolean;
  publicCode?: string | null;
  isOwner?: boolean;
}

// Local subscription data
interface LocalSubscription {
  id?: string;
  publicCode: string;
  name: string;
}

// Default initial elements (flat structure with sortOrder and anchorOffset)
// These match the old default phases structure:
// - Countdown (10 min), Worship (18 min), 300 (5 min) - all chained (no anchorOffset)
// - Message (30 min) - anchored at offset 1380 (23 min from occurrence start)
const DEFAULT_ELEMENTS: Element[] = [
  {
    id: crypto.randomUUID(),
    timelineId: '',
    parentId: null,
    description: 'Countdown',
    duration: 10 * 60,
    anchorOffset: null, // Chained element
    sortOrder: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: crypto.randomUUID(),
    timelineId: '',
    parentId: null,
    description: 'Worship',
    duration: 18 * 60,
    anchorOffset: null, // Chained element
    sortOrder: 1,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: crypto.randomUUID(),
    timelineId: '',
    parentId: null,
    description: '300',
    duration: 5 * 60,
    anchorOffset: null, // Chained element
    sortOrder: 2,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: crypto.randomUUID(),
    timelineId: '',
    parentId: null,
    description: 'Message',
    duration: 30 * 60,
    anchorOffset: 23 * 60, // Anchored at 23 minutes from occurrence start
    sortOrder: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

// Default schedule (UI format)
const DEFAULT_SCHEDULE: Schedule = {
  sunday: ['08:30:00', '10:00:00', '11:30:00', '13:00:00'],
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: []
};

// ============================================================================
// Context Value Type
// ============================================================================

interface TimelineContextValue {
  // Loading states
  stateLoaded: boolean;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;

  // Current timeline
  currentTimelineId: string | null;
  timelineName: string;
  isTimelinePublic: boolean;
  timelinePublicCode: string;
  isOwner: boolean;
  isSubscribed: boolean;

  // All timelines
  allTimelines: TimelineListItem[];

  // Schedule (UI uses Schedule format, converted to/from scheduleEntries at API boundary)
  schedule: Schedule;
  setSchedule: React.Dispatch<React.SetStateAction<Schedule>>;
  selectedStartTime: string;
  timeOffset: number;
  setTimeOffset: React.Dispatch<React.SetStateAction<number>>;
  activeStartTime: string | null;
  isAutoAdvanceEnabled: boolean;
  setIsAutoAdvanceEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  autoAdvanceBuffer: number;
  setAutoAdvanceBuffer: React.Dispatch<React.SetStateAction<number>>;

  // Elements (flat array with sortOrder and anchorOffset)
  elements: Element[];

  // Computed values
  currentTimeSeconds: number;
  scheduledOccurrenceTimes: ScheduledOccurrence[];
  upcomingTimes: string[];
  noUpcomingOccurrences: boolean;
  clientAutoAdvanceTime: string | null;
  effectiveStartTime: string | null;
  effectiveStartAbsolute: number | null;
  effectiveDayName: string | null;
  isOccurrencePast: boolean;

  // Timeline CRUD handlers
  handleTimelineSwitch: (timelineId: string) => Promise<void>;
  handleCreateNewTimeline: () => Promise<void>;
  handleDeleteTimeline: () => Promise<void>;
  handleTimelineNameChange: (newName: string) => Promise<void>;
  handleTogglePublic: () => Promise<void>;
  handlePublicCodeChange: (newCode: string) => void;

  // Subscription handlers
  handleSubscribe: () => Promise<void>;
  handleUnsubscribe: (timeline: TimelineListItem) => Promise<void>;

  // Schedule handlers
  handleNextStartTime: (e?: React.MouseEvent) => void;
  handlePrevStartTime: (e?: React.MouseEvent) => void;

  // Element handlers (new flat structure)
  handleAddElement: (element: Partial<ElementInput>) => void;
  handleUpdateElement: (elementId: string, updates: Partial<ElementInput>) => void;
  handleDeleteElement: (elementId: string) => void;
  handleReorderElements: (fromIndex: number, toIndex: number) => void;
  handleMoveElement: (draggedId: string, targetId: string, position: DropPosition, groupName?: string) => void;
  handleUngroupElement: (groupId: string) => void;

  // Defaults handlers
  handleSetDefaults: () => Promise<void>;
  handleLoadDefaults: () => Promise<void>;
}

const TimelineContext = createContext<TimelineContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface TimelineProviderProps {
  children: ReactNode;
}

export const TimelineProvider: React.FC<TimelineProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { getTimeWithOffset } = useTime();
  const { code: publicCode } = useParams<{ code?: string }>();
  const navigate = useNavigate();

  // Loading states
  const [stateLoaded, setStateLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs
  const pendingServerUpdateRef = useRef(false);
  const saveTimelineIdRef = useRef<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Timeline state
  const [currentTimelineId, setCurrentTimelineId] = useState<string | null>(null);
  const [allTimelines, setAllTimelines] = useState<TimelineListItem[]>([]);
  const [timelineName, setTimelineName] = useState('Untitled Timeline');
  const [isTimelinePublic, setIsTimelinePublic] = useState(false);
  const [timelinePublicCode, setTimelinePublicCode] = useState('');
  const [isOwner, setIsOwner] = useState(true);
  const [isSubscribed, setIsSubscribed] = useState(false);

  // Schedule state
  const [schedule, setSchedule] = useState<Schedule>(DEFAULT_SCHEDULE);
  const [selectedStartTime, setSelectedStartTime] = useState('');
  const [timeOffset, setTimeOffset] = useState(0);
  const [activeStartTime, setActiveStartTime] = useState<string | null>(null);
  const [isAutoAdvanceEnabled, setIsAutoAdvanceEnabled] = useState(true);
  const [autoAdvanceBuffer, setAutoAdvanceBuffer] = useState(300);

  // Elements state (flat array with sortOrder and anchorOffset)
  const [elements, setElements] = useState<Element[]>(DEFAULT_ELEMENTS);

  // Computed: current time
  const currentTimeSeconds = getTimeWithOffset(timeOffset);

  // Computed: scheduled occurrences
  const scheduledOccurrenceTimes = getOccurrencesInWindow(
    schedule,
    currentTimeSeconds,
    timeSyncService.getSyncedDate().getDay()
  );

  // Computed: upcoming times (for backward compatibility)
  const upcomingTimes = scheduledOccurrenceTimes.map(st => st.time);

  // Keep saveTimelineIdRef in sync
  useEffect(() => {
    saveTimelineIdRef.current = currentTimelineId;
  }, [currentTimelineId]);

  // Initialize time sync
  useEffect(() => {
    const initializeTimeSync = async () => {
      console.log('Initializing time synchronization...');
      await timeSyncService.initialize();
      console.log('Time sync initialized. Server offset:', timeSyncService.getServerOffset(), 'ms');

      const syncInterval = setInterval(() => {
        timeSyncService.checkAndSync();
      }, 60000);

      return () => clearInterval(syncInterval);
    };

    initializeTimeSync();
  }, []);

  // Helper to load timeline data into state
  const loadTimelineIntoState = useCallback((timeline: TimelineData, userId: string | null = null) => {
    if (!timeline) return;

    setCurrentTimelineId(timeline.id);
    setTimelineName(timeline.name || 'Untitled Timeline');
    setIsTimelinePublic(timeline.isPublic || false);
    setTimelinePublicCode(timeline.publicCode || '');
    setIsOwner(userId ? timeline.ownerId === userId : false);
    setIsSubscribed(timeline.isSubscribed || false);

    // Load elements (flat array with sortOrder and anchorOffset)
    if (timeline.elements) {
      setElements(timeline.elements);
    }
    // Convert scheduleEntries to Schedule format for UI
    if (timeline.scheduleEntries) {
      setSchedule(entriesToSchedule(timeline.scheduleEntries));
    }
    if (timeline.timeOffset !== undefined && timeline.timeOffset !== null) {
      setTimeOffset(timeline.timeOffset);
    } else if (timeline.timeOffset === null) {
      setTimeOffset(0);
    }
    if (timeline.selectedStartTime !== undefined && timeline.selectedStartTime !== null) {
      setSelectedStartTime(timeline.selectedStartTime);
    }
    if (timeline.activeStartTime !== undefined) {
      setActiveStartTime(timeline.activeStartTime);
    }
    if (timeline.isAutoAdvanceEnabled !== undefined) {
      setIsAutoAdvanceEnabled(timeline.isAutoAdvanceEnabled);
    }
    if (timeline.autoAdvanceBuffer !== undefined) {
      setAutoAdvanceBuffer(timeline.autoAdvanceBuffer);
    }
  }, []);

  // Build timeline list for picker
  const buildTimelineList = useCallback(async (userId: string | null | undefined): Promise<TimelineListItem[]> => {
    const timelines: TimelineListItem[] = [];

    if (userId) {
      try {
        const serverTimelines = await timelineService.getAllUserTimelines(userId);
        timelines.push(...serverTimelines);
      } catch (error) {
        console.error('Failed to load server timelines:', error);
      }
    } else {
      const hasLocalData = stateService.loadFromLocal();
      if (hasLocalData) {
        timelines.push({
          id: 'local',
          name: 'My Timeline',
          isLocal: true,
          isSubscribed: false
        });
      }
    }

    if (!userId) {
      const localSubs = getLocalSubscriptions() as LocalSubscription[];
      for (const sub of localSubs) {
        if (!timelines.find(t => t.publicCode === sub.publicCode)) {
          timelines.push({
            id: sub.id || `local-sub-${sub.publicCode}`,
            name: sub.name,
            publicCode: sub.publicCode,
            isSubscribed: true,
            isLocalSubscription: true
          });
        }
      }
    }

    return timelines;
  }, []);

  // Load state from cloud/local storage
  useEffect(() => {
    const loadState = async () => {
      setLoading(true);
      setError(null);
      try {
        const userId = currentUser?.id;

        if (publicCode) {
          console.log('Loading public timeline:', publicCode);
          const timeline = await timelineService.getTimelineByPublicCode(publicCode);

          if (timeline) {
            pendingServerUpdateRef.current = true;
            loadTimelineIntoState(timeline, userId);

            if (!userId) {
              addLocalSubscription(publicCode, timeline.name, timeline.id);
            }
          } else {
            setError('Timeline not found');
          }

          const timelines = await buildTimelineList(userId);
          setAllTimelines(timelines);

          setLoading(false);
          setStateLoaded(true);
          return;
        }

        let timelineId: string | null = null;

        if (userId) {
          const localSubs = getLocalSubscriptions() as LocalSubscription[];
          if (localSubs.length > 0) {
            console.log('Migrating local subscriptions to server:', localSubs.length);
            try {
              const result = await timelineService.migrateLocalSubscriptions(localSubs);
              console.log('Migration complete:', result);
              clearLocalSubscriptions();
            } catch (migrationError) {
              console.error('Failed to migrate local subscriptions:', migrationError);
            }
          }

          timelineId = await timelineService.getUserActiveTimeline(userId);

          if (!timelineId) {
            const localState = stateService.loadFromLocal();
            const createData = localState ? {
              elements: localState.elements,
              scheduleEntries: localState.schedule ? scheduleToEntries(localState.schedule) : undefined
            } : {};
            timelineId = await timelineService.createTimeline(userId, createData);
            console.log('Created new timeline:', timelineId);
          }

          if (timelineId) {
            const timeline = await timelineService.getTimeline(timelineId);
            if (timeline) {
              loadTimelineIntoState(timeline, userId);
            }
          }

          const timelines = await buildTimelineList(userId);
          setAllTimelines(timelines);
        } else {
          const savedState = stateService.loadFromLocal();
          if (savedState) {
            if (savedState.elements) {
              setElements(savedState.elements);
            }
            if (savedState.schedule) {
              setSchedule(savedState.schedule);
            }
            if (savedState.timeOffset !== undefined) {
              setTimeOffset(savedState.timeOffset);
            }
          }

          const timelines = await buildTimelineList(null);
          setAllTimelines(timelines);
        }
      } catch (err) {
        console.error('Failed to load state:', err);
        setError('Failed to load timeline data');
      } finally {
        setLoading(false);
        setStateLoaded(true);
      }
    };

    loadState();
  }, [currentUser, publicCode, loadTimelineIntoState, buildTimelineList]);

  // Subscribe to real-time timeline updates
  useEffect(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    if (currentTimelineId) {
      console.log('Subscribing to timeline updates:', currentTimelineId);
      unsubscribeRef.current = timelineService.subscribeToTimeline(currentTimelineId, (timeline, sourceClientId) => {
        if (!timeline) return;

        const reason = (timeline as TimelineData)._reason || 'unknown';
        const isOwnUpdate = sourceClientId === getClientId();
        console.log(`Received timeline update (reason: ${reason}, isOwner: ${isOwner}, isOwnUpdate: ${isOwnUpdate})`);

        // Handle activeStartTime from partial updates (e.g., auto-advance events)
        // These updates may not include 'id' but should still be processed
        if (timeline.activeStartTime !== undefined) {
          setActiveStartTime(timeline.activeStartTime);
        }

        // For full timeline updates (with id), process all fields
        if ('id' in timeline) {
          if (timeline.name !== undefined) {
            setTimelineName(timeline.name || 'Untitled Timeline');
          }
          // Only apply updates from OTHER clients (prevents echo-back of own edits)
          // This allows multi-device editing: iOS edits update webapp, and vice versa
          if (!isOwnUpdate) {
            pendingServerUpdateRef.current = true;

            if (timeline.elements) {
              setElements(timeline.elements);
            }
            if (timeline.scheduleEntries) {
              setSchedule(entriesToSchedule(timeline.scheduleEntries));
            }
            if (timeline.timeOffset !== undefined && timeline.timeOffset !== null) {
              setTimeOffset(timeline.timeOffset);
            } else if (timeline.timeOffset === null) {
              setTimeOffset(0);
            }
            if (timeline.selectedStartTime !== undefined && timeline.selectedStartTime !== null && timeline.selectedStartTime !== '') {
              setSelectedStartTime(timeline.selectedStartTime);
            }
            if (timeline.isAutoAdvanceEnabled !== undefined) {
              setIsAutoAdvanceEnabled(timeline.isAutoAdvanceEnabled);
            }
            if (timeline.autoAdvanceBuffer !== undefined) {
              setAutoAdvanceBuffer(timeline.autoAdvanceBuffer);
            }
          }
          setIsTimelinePublic(timeline.isPublic || false);
        }
      });
    }

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [currentTimelineId, isOwner]);

  // Save state (debounced)
  useEffect(() => {
    console.log('[Save] Effect triggered', { stateLoaded, isOwner, pendingServerUpdate: pendingServerUpdateRef.current });

    if (!stateLoaded) {
      console.log('[Save] Skipped: stateLoaded is false');
      return;
    }
    if (!isOwner) {
      console.log('[Save] Skipped: not owner');
      return;
    }

    if (pendingServerUpdateRef.current) {
      console.log('[Save] Skipped: pendingServerUpdate flag is set');
      pendingServerUpdateRef.current = false;
      return;
    }

    const saveState = async () => {
      if (pendingServerUpdateRef.current) {
        console.log('[Save] Skipped in timeout: pendingServerUpdate flag is set');
        pendingServerUpdateRef.current = false;
        return;
      }

      const timelineIdToSave = saveTimelineIdRef.current;

      const state = {
        schedule,
        elements,
        selectedStartTime,
        timeOffset,
        isAutoAdvanceEnabled,
        autoAdvanceBuffer
      };

      console.log('[Save] Saving', timelineIdToSave ? `to timeline: ${timelineIdToSave}` : 'to localStorage only', 'elements count:', elements.length);

      try {
        await stateService.save(timelineIdToSave, state);
        console.log('[Save] Success');
      } catch (err: unknown) {
        console.error('Failed to save state:', err);

        // Check if this is a conflict error (only possible with cloud saves)
        const error = err as { code?: string; serverValue?: Record<string, unknown> };
        if (error.code === 'CONFLICT' && timelineIdToSave) {
          console.log('[Save] Conflict detected, reloading timeline from server');
          // Reload timeline from server to get fresh data
          try {
            const freshTimeline = await timelineService.getTimeline(timelineIdToSave);
            if (freshTimeline) {
              pendingServerUpdateRef.current = true;
              loadTimelineIntoState(freshTimeline, currentUser?.id);
              console.log('[Save] Reloaded timeline after conflict');
            }
          } catch (reloadErr) {
            console.error('Failed to reload timeline after conflict:', reloadErr);
            setError('Timeline was updated elsewhere. Please refresh.');
          }
        } else if (timelineIdToSave) {
          // Only show error for cloud save failures, not localStorage
          setError('Failed to save changes');
        }
      }
    };

    console.log('[Save] Scheduling save in 1 second');
    const timeoutId = setTimeout(saveState, 1000);
    return () => clearTimeout(timeoutId);
  }, [schedule, elements, selectedStartTime, timeOffset, isAutoAdvanceEnabled, autoAdvanceBuffer, stateLoaded, isOwner, loadTimelineIntoState, currentUser?.id]);

  // Auto-select start time
  useEffect(() => {
    if (!stateLoaded) return;

    if (upcomingTimes.length === 0) {
      if (selectedStartTime) {
        setSelectedStartTime('');
      }
      return;
    }

    const shouldAutoSelect = (
      !selectedStartTime ||
      selectedStartTime.trim() === '' ||
      !upcomingTimes.includes(selectedStartTime)
    );

    if (shouldAutoSelect) {
      setSelectedStartTime(upcomingTimes[0]);
    }
  }, [upcomingTimes, stateLoaded, selectedStartTime]);

  // Sync isSubscribed state
  useEffect(() => {
    if (!currentTimelineId || allTimelines.length === 0) return;

    const timelineInList = allTimelines.find(t => t.id === currentTimelineId);
    if (timelineInList) {
      setIsSubscribed(timelineInList.isSubscribed || false);
    }
  }, [currentTimelineId, allTimelines]);

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleTimelineSwitch = async (timelineId: string) => {
    const userId = currentUser?.id;
    const targetTimeline = allTimelines.find(t => t.id === timelineId);

    if (timelineId === 'local' || targetTimeline?.isLocal) {
      navigate('/');
      return;
    }

    if (targetTimeline?.isLocalSubscription && targetTimeline?.publicCode) {
      navigate(`/t/${targetTimeline.publicCode}`);
      return;
    }

    if (!userId) {
      if (targetTimeline?.publicCode) {
        navigate(`/t/${targetTimeline.publicCode}`);
      }
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await timelineService.setActiveTimeline(userId, timelineId);
      const timeline = await timelineService.getTimeline(timelineId);
      if (timeline) {
        pendingServerUpdateRef.current = true;

        if (timeline.publicCode && timeline.ownerId !== userId) {
          navigate(`/t/${timeline.publicCode}`);
          return;
        }

        if (publicCode) {
          navigate('/');
        }

        loadTimelineIntoState(timeline, userId);
        console.log('Switched to timeline:', timelineId);
      }
    } catch (err) {
      console.error('Failed to switch timeline:', err);
      setError('Failed to switch timeline. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewTimeline = async () => {
    const userId = currentUser?.id;
    if (!userId) {
      setError('Please log in to create timelines');
      return;
    }

    const newName = prompt('Enter a name for the new timeline:', 'New Timeline');
    if (!newName) return;

    setLoading(true);
    setError(null);
    try {
      const timelineId = await timelineService.createTimeline(userId, {
        name: newName,
        autoActivate: false,
      });

      const timelines = await timelineService.getAllUserTimelines(userId);
      setAllTimelines(timelines);

      console.log('Created new timeline:', timelineId);
      alert(`Timeline "${newName}" created! Use the timeline switcher to access it.`);
    } catch (err) {
      console.error('Failed to create timeline:', err);
      setError('Failed to create timeline. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeline = async () => {
    const userId = currentUser?.id;
    if (!userId) {
      setError('Please log in to delete timelines');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const timelineIdToDelete = currentTimelineId;
      if (!timelineIdToDelete) return;

      await timelineService.deleteTimeline(timelineIdToDelete);

      const updatedTimelines = allTimelines.filter(t => t.id !== timelineIdToDelete);
      setAllTimelines(updatedTimelines);

      if (updatedTimelines.length > 0) {
        const nextTimeline = updatedTimelines[0];
        await handleTimelineSwitch(nextTimeline.id);
      } else {
        console.error('No timelines remaining after delete');
      }

      console.log('Deleted timeline:', timelineIdToDelete);
    } catch (err) {
      console.error('Failed to delete timeline:', err);
      setError('Failed to delete timeline. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (timeline: TimelineListItem) => {
    const userId = currentUser?.id;

    try {
      if (userId) {
        await timelineService.unsubscribeFromTimeline(timeline.id);
      } else {
        removeLocalSubscription(timeline.publicCode);
      }

      const updatedTimelines = await buildTimelineList(userId);
      setAllTimelines(updatedTimelines);

      if (timeline.id === currentTimelineId ||
          (timeline.publicCode && timeline.publicCode === publicCode)) {
        if (updatedTimelines.length > 0) {
          const nextTimeline = updatedTimelines[0];
          await handleTimelineSwitch(nextTimeline.id);
        } else {
          navigate('/');
        }
      }

      console.log('Unsubscribed from timeline:', timeline.name);
    } catch (err) {
      console.error('Failed to unsubscribe from timeline:', err);
      setError('Failed to unsubscribe. Please try again.');
    }
  };

  const handleSubscribe = async () => {
    const userId = currentUser?.id;
    if (!userId || !currentTimelineId) return;

    try {
      await timelineService.subscribeToTimelineById(currentTimelineId);
      setIsSubscribed(true);

      const updatedTimelines = await buildTimelineList(userId);
      setAllTimelines(updatedTimelines);

      console.log('Subscribed to timeline:', timelineName);
    } catch (err) {
      console.error('Failed to subscribe to timeline:', err);
      setError('Failed to subscribe. Please try again.');
    }
  };

  // Element handlers (new flat structure with sortOrder and anchorOffset)
  const handleAddElement = (elementData: Partial<ElementInput>) => {
    const newElement: Element = {
      id: crypto.randomUUID(),
      timelineId: currentTimelineId || '',
      parentId: null,
      description: elementData.description || 'New Element',
      duration: elementData.duration || 5 * 60,
      anchorOffset: elementData.anchorOffset ?? null, // null = chained, number = anchored
      sortOrder: elements.length,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setElements(prev => [...prev, newElement]);
  };

  const handleUpdateElement = (elementId: string, updates: Partial<ElementInput>) => {
    setElements(prevElements =>
      prevElements.map(el =>
        el.id === elementId
          ? { ...el, ...updates, updatedAt: new Date().toISOString() }
          : el
      )
    );
  };

  const handleDeleteElement = (elementId: string) => {
    setElements(prevElements => {
      const filtered = prevElements.filter(el => el.id !== elementId);
      // Re-normalize sortOrder after deletion
      return filtered.map((el, idx) => ({ ...el, sortOrder: idx }));
    });
  };

  const handleReorderElements = (fromIndex: number, toIndex: number) => {
    setElements(prevElements => {
      // Sort by sortOrder first to match the visual order (drag indices come from sorted view)
      const sorted = [...prevElements].sort((a, b) => a.sortOrder - b.sortOrder);
      const [removed] = sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, removed);
      // Re-normalize sortOrder after reorder
      return sorted.map((el, idx) => ({ ...el, sortOrder: idx }));
    });
  };

  // Move an element relative to a target (sibling reorder OR group via drop-onto).
  // position 'into' a leaf wraps both in a new group (groupName supplied by the UI).
  const handleMoveElement = (
    draggedId: string,
    targetId: string,
    position: DropPosition,
    groupName?: string
  ) => {
    setElements(prevElements => {
      const { elements: next } = moveElementOp(prevElements, draggedId, targetId, position, {
        newGroupId: crypto.randomUUID(),
        groupName,
        now: new Date().toISOString(),
      });
      return next;
    });
  };

  // Dissolve a group: children become siblings of the group, group removed.
  const handleUngroupElement = (groupId: string) => {
    setElements(prevElements => ungroupOp(prevElements, groupId));
  };

  const handleNextStartTime = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (upcomingTimes.length === 0) return;
    const currentIndex = upcomingTimes.findIndex(time => time === selectedStartTime);
    if (currentIndex === -1) {
      setSelectedStartTime(upcomingTimes[0]);
    } else {
      const nextIndex = (currentIndex + 1) % upcomingTimes.length;
      setSelectedStartTime(upcomingTimes[nextIndex]);
    }
  };

  const handlePrevStartTime = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (upcomingTimes.length === 0) return;
    const currentIndex = upcomingTimes.findIndex(time => time === selectedStartTime);
    if (currentIndex === -1) {
      setSelectedStartTime(upcomingTimes[upcomingTimes.length - 1]);
    } else {
      const prevIndex = (currentIndex - 1 + upcomingTimes.length) % upcomingTimes.length;
      setSelectedStartTime(upcomingTimes[prevIndex]);
    }
  };

  const handleSetDefaults = async () => {
    if (!currentUser) return;

    const state = { schedule, elements, selectedStartTime };

    try {
      await defaultsService.saveDefaults(currentUser.id, state);
      alert('Defaults saved successfully!');
    } catch (error) {
      console.error('Failed to save defaults:', error);
      alert('Failed to save defaults. Please try again.');
    }
  };

  const handleLoadDefaults = async () => {
    if (!currentUser) return;

    try {
      const defaults = await defaultsService.loadDefaults(currentUser.id);

      if (defaults) {
        if (defaults.schedule) {
          setSchedule(defaults.schedule);
        }
        if (defaults.elements) {
          setElements(defaults.elements);
        }
        if (defaults.selectedStartTime) {
          setSelectedStartTime(defaults.selectedStartTime);
        }
        alert('Defaults loaded successfully!');
      } else {
        alert('No defaults found. Please set defaults first.');
      }
    } catch (error) {
      console.error('Failed to load defaults:', error);
      alert('Failed to load defaults. Please try again.');
    }
  };

  const handleTogglePublic = async () => {
    if (!currentTimelineId) {
      alert('No active timeline found. Please log in to publish your timeline.');
      return;
    }

    try {
      const newPublicState = !isTimelinePublic;
      const updatedTimeline = await timelineService.setTimelinePublic(currentTimelineId, newPublicState);
      setIsTimelinePublic(newPublicState);

      if (updatedTimeline && updatedTimeline.publicCode) {
        setTimelinePublicCode(updatedTimeline.publicCode);
      }

      if (newPublicState) {
        alert('Timeline is now public! The QR code is displayed on the countdown timer.');
      } else {
        alert('Timeline is now private.');
      }
    } catch (error) {
      console.error('Failed to toggle timeline visibility:', error);
      alert('Failed to update timeline visibility. Please try again.');
    }
  };

  const handlePublicCodeChange = (newCode: string) => {
    setTimelinePublicCode(newCode);
  };

  const handleTimelineNameChange = async (newName: string) => {
    if (!currentTimelineId) {
      console.warn('No active timeline to update');
      return;
    }

    try {
      setTimelineName(newName);
      await timelineService.updateTimeline(currentTimelineId, { name: newName });
      console.log('Timeline name updated:', newName);
    } catch (error) {
      console.error('Failed to update timeline name:', error);
      alert('Failed to update timeline name. Please try again.');
    }
  };

  // ============================================================================
  // Computed Values
  // ============================================================================

  // Check if there are any upcoming occurrences that haven't ended yet
  const noUpcomingOccurrences = useMemo(() => {
    if (scheduledOccurrenceTimes.length === 0) return true;
    // Check if any occurrence's end time is in the future
    return !scheduledOccurrenceTimes.some(occurrence => {
      const endTime = calculateOccurrenceEndTimeFromElements(occurrence.absoluteSeconds, elements);
      return endTime !== null && endTime > currentTimeSeconds;
    });
  }, [scheduledOccurrenceTimes, elements, currentTimeSeconds]);

  const clientAutoAdvanceTime = useMemo(() => {
    if (!isAutoAdvanceEnabled || scheduledOccurrenceTimes.length === 0) return null;
    return determineActiveOccurrenceFromElements(
      currentTimeSeconds,
      scheduledOccurrenceTimes,
      elements,
      autoAdvanceBuffer
    );
  }, [isAutoAdvanceEnabled, scheduledOccurrenceTimes, elements, autoAdvanceBuffer, currentTimeSeconds]);

  const firstFutureOccurrence = scheduledOccurrenceTimes.find(st => st.absoluteSeconds > currentTimeSeconds);
  const lastScheduledOccurrence = scheduledOccurrenceTimes[scheduledOccurrenceTimes.length - 1];

  const isTimeInSchedule = (time: string | null): boolean => {
    return !!time && scheduledOccurrenceTimes.some(st => st.time === time);
  };

  const candidateStartTime = isAutoAdvanceEnabled
    ? (activeStartTime || clientAutoAdvanceTime || firstFutureOccurrence?.time || null)
    : (selectedStartTime || firstFutureOccurrence?.time || lastScheduledOccurrence?.time || null);

  const effectiveStartTime = isTimeInSchedule(candidateStartTime)
    ? candidateStartTime
    : (firstFutureOccurrence?.time || null);

  const effectiveScheduledOccurrence = scheduledOccurrenceTimes.find(st => st.time === effectiveStartTime);
  const effectiveStartAbsolute = effectiveScheduledOccurrence ? effectiveScheduledOccurrence.absoluteSeconds : null;
  const effectiveDayName = effectiveScheduledOccurrence ? effectiveScheduledOccurrence.dayName : null;

  // Check if the current occurrence has ended
  const isOccurrencePast = useMemo(() => {
    if (isAutoAdvanceEnabled || effectiveStartTime === null) return false;
    const startAbsolute = effectiveStartAbsolute ?? parseTimeToSeconds(effectiveStartTime);
    const endTime = calculateOccurrenceEndTimeFromElements(startAbsolute, elements);
    return endTime !== null && currentTimeSeconds >= endTime;
  }, [isAutoAdvanceEnabled, effectiveStartTime, effectiveStartAbsolute, elements, currentTimeSeconds]);

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: TimelineContextValue = {
    // Loading states
    stateLoaded,
    loading,
    error,
    setError,

    // Current timeline
    currentTimelineId,
    timelineName,
    isTimelinePublic,
    timelinePublicCode,
    isOwner,
    isSubscribed,

    // All timelines
    allTimelines,

    // Schedule
    schedule,
    setSchedule,
    selectedStartTime,
    timeOffset,
    setTimeOffset,
    activeStartTime,
    isAutoAdvanceEnabled,
    setIsAutoAdvanceEnabled,
    autoAdvanceBuffer,
    setAutoAdvanceBuffer,

    // Elements (flat array with sortOrder and anchorOffset)
    elements,

    // Computed values
    currentTimeSeconds,
    scheduledOccurrenceTimes,
    upcomingTimes,
    noUpcomingOccurrences,
    clientAutoAdvanceTime,
    effectiveStartTime,
    effectiveStartAbsolute,
    effectiveDayName,
    isOccurrencePast,

    // Timeline CRUD handlers
    handleTimelineSwitch,
    handleCreateNewTimeline,
    handleDeleteTimeline,
    handleTimelineNameChange,
    handleTogglePublic,
    handlePublicCodeChange,

    // Subscription handlers
    handleSubscribe,
    handleUnsubscribe,

    // Schedule handlers
    handleNextStartTime,
    handlePrevStartTime,

    // Element handlers (new flat structure)
    handleAddElement,
    handleUpdateElement,
    handleDeleteElement,
    handleReorderElements,
    handleMoveElement,
    handleUngroupElement,

    // Defaults handlers
    handleSetDefaults,
    handleLoadDefaults,
  };

  return (
    <TimelineContext.Provider value={value}>
      {children}
    </TimelineContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

export const useTimeline = (): TimelineContextValue => {
  const context = useContext(TimelineContext);
  if (!context) {
    throw new Error('useTimeline must be used within a TimelineProvider');
  }
  return context;
};

export default TimelineContext;
